import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { AgentSelector } from "@/components/AgentSelector";
import {
  ACTION_PLAN_LAST_AGENT_KEY,
  getDefaultAgentId,
  setLastUsedAgentId,
} from "@/lib/threadActionAgents";

interface ActionPlanDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Runs the agent and creates the document. Should throw on failure so the dialog stays open. */
  onCreate: (agentId: string) => Promise<void>;
}

export function ActionPlanDialog({ open, onOpenChange, onCreate }: ActionPlanDialogProps) {
  const [agentId, setAgentId] = useState<string | null>(null);
  const [running, setRunning] = useState(false);

  // Default to the last-used agent (localStorage), else a plan-flavored built-in.
  useEffect(() => {
    if (open) {
      setAgentId(
        (prev) => prev ?? getDefaultAgentId(ACTION_PLAN_LAST_AGENT_KEY, /action\s*plan|planner|task/i)
      );
    }
  }, [open]);

  const handleCreate = async () => {
    if (!agentId || running) return;
    setRunning(true);
    try {
      setLastUsedAgentId(ACTION_PLAN_LAST_AGENT_KEY, agentId);
      await onCreate(agentId);
      onOpenChange(false);
    } catch {
      // Error toast is shown by the caller; keep the dialog open for retry (G4).
    } finally {
      setRunning(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !running && onOpenChange(next)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create Action Plan</DialogTitle>
          <DialogDescription>
            The selected agent converts the latest AI response into a task list document in the
            current space and topic.
          </DialogDescription>
        </DialogHeader>
        <div className="py-2 space-y-2">
          <Label>Select Agent</Label>
          <AgentSelector
            value={agentId}
            onValueChange={setAgentId}
            isActive={open}
            disabled={running}
          />
          {!agentId && (
            <p className="text-xs text-muted-foreground">
              No agents available — pick a built-in agent above or create one in the Agents hub.
            </p>
          )}
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={running}
            data-testid="button-action-plan-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={!agentId || running}
            data-testid="button-action-plan-create"
          >
            {running ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              "Create Task List"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
